import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/pro-solid-svg-icons/faCalculator';

import React, { useCallback, useMemo, useState } from 'react';

import classnames from 'clsx';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';

import { messages } from './messages';
import { OpenRateBrowserHotkey } from '../../components/hotkeys/advanced-hotkeys/open-rate-calc';
import { ModalPortal } from '../../components/modal-portal';
import { RateViewerModal } from '../../components/modals/rate-viewer-modal';
import { useTrackRateBrowser } from '../../components/rate-viewer/use-track-rate-browser';
import { EXTERNAL } from '../../constants/fulfillment-types';
import { WeightUnit } from '../../enums/weight-unit';
import { useModal } from '../../hooks/use-modal';
import { isResidentialAddress } from '../../services/address-validation-service';
import { PackageService } from '../../services/package-service';
import { updateConfigureRatesModel } from '../../store/rate-browser/rate-browser.actions';
import * as RateViewerActions from '../../store/rate-viewer/rate-viewer.actions';
import { useFulfillmentPlannerContext } from '../context';

import style from './style.module.css';
import { WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { EventSourceStrings } from '../../tracking/tracking-constants';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { useEnableMultiPackageShipmentsOrderDetails } from '../../feature-flags/hooks/use-multi-package-shipping-order-details';
import { Text, TextType } from 'ui-components/components/text';

const mapWeightFromRateBrowser = ({ weightUnit, weight }) => ({
  unit: weightUnit || WeightUnit.Ounces,
  value: weight || 0,
});

const mapWeightToRateBrowserModel = (packageWeight) => ({
  weight: packageWeight?.value,
  weightUnit: packageWeight?.unit,
});

export const RateBrowserButton = ({ className = null, forceTextButton = false, iconClassName, isOnOrderDetails = false }) => {
  const {
    fulfillmentPlanId,
    fulfillmentType,
    shipFromId,
    updateFPFromRateBrowserConfigureLabel,
    onWeightAndDimensionsChange,
    otherShippingOptions,
    packages,
    shipTo,
  } = useFulfillmentPlannerContext();
  const onScaleMessage = useCallback(
    (weightAndDims) => {
      onWeightAndDimensionsChange(weightAndDims?.weight, weightAndDims?.dims);
    },
    [onWeightAndDimensionsChange],
  );
  const isExternalFulfillment = fulfillmentType === EXTERNAL;
  const { isOpen, open, close } = useModal();
  const trackRateBrowser = useTrackRateBrowser();

  const shipToCountryCode = shipTo?.countryCode;
  const shipToPostalCode = shipTo?.postalCode;
  const shipToCityOrSuburb = shipTo?.city;
  const shipToResidentialIndicator = shipTo?.residentialIndicator;
  const confirmationType = otherShippingOptions.confirmationEnumId?.toString();

  const model = useMemo(() => {
    return {
      shipFromId,
      weight: mapWeightToRateBrowserModel(packages[0]?.weight),
      dimensions: packages[0]?.dimensions,
      packageTypeId: packages[0]?.packageTypeId || PackageService.getStandardPackageId(),
      shipToCity: shipToCityOrSuburb,
      shipToCityOrSuburb,
      shipToCountryCode,
      shipToPostalCode,
      isResidential: isResidentialAddress(shipToResidentialIndicator),
      confirmationType,
      packages,
    };
  }, [shipFromId, packages, shipToCityOrSuburb, shipToCountryCode, shipToPostalCode, shipToResidentialIndicator, confirmationType]);

  const [modelFromRateBrowser, setModelFromRateBrowser] = useState(model);

  const dispatch = useDispatch();
  const onLaunch = useCallback(() => {
    const mappedModel = {
      ...model,
      weight: mapWeightFromRateBrowser(model.weight),
    };
    dispatch(updateConfigureRatesModel(model));
    setModelFromRateBrowser(mappedModel);
    dispatch(RateViewerActions.loadRates(fulfillmentPlanId, mappedModel));
    open();
  }, [dispatch, fulfillmentPlanId, model, open]);

  const onLaunchFromButtonClick = useCallback(() => {
    onLaunch();

    if (isOnOrderDetails) {
      trackRateBrowser(WorkflowSteps.START, {
        source: EventSourceStrings.ORDER_DETAILS_CSW,
      });
    } else {
      trackRateBrowser(WorkflowSteps.START, {
        source: EventSourceStrings.GRID_CSW,
      });
    }
  }, [isOnOrderDetails, onLaunch, trackRateBrowser]);

  const onClose = useCallback(() => {
    trackRateBrowser(WorkflowSteps.COMPLETE);
    close();
    dispatch(RateViewerActions.clearRateViewer(fulfillmentPlanId));
  }, [close, dispatch, fulfillmentPlanId, trackRateBrowser]);

  const onConfigureLabel = useCallback(
    (rateBrowserSelection) => {
      updateFPFromRateBrowserConfigureLabel({
        modelFromRateBrowser,
        rateBrowserSelection,
      });
      onClose();
    },
    [modelFromRateBrowser, onClose, updateFPFromRateBrowserConfigureLabel],
  );

  const onBrowseRates = useCallback(
    (request) => {
      trackRateBrowser(WorkflowSteps.RATE_BROWSER_BROWSE_RATES_CLICKED);
      const mappedModel = {
        ...request,
        weight: mapWeightFromRateBrowser(request.weight),
      };
      setModelFromRateBrowser(mappedModel);
      dispatch(RateViewerActions.loadRates(fulfillmentPlanId, mappedModel));
    },
    [dispatch, fulfillmentPlanId, trackRateBrowser],
  );

  const enableMultiPackageShipmentsOrderDetails = useEnableMultiPackageShipmentsOrderDetails();

  // TODO: use this version when the feature flag is removed
  if (enableMultiPackageShipmentsOrderDetails || forceTextButton) {
    return (
      <>
        <OpenRateBrowserHotkey
          fulfillmentPlanId={fulfillmentPlanId}
          isOnOrderDetails={isOnOrderDetails}
          onLaunchRateBrowserFromOrderDetails={onLaunch}
        >
          <Button
            className={className}
            disabled={isExternalFulfillment}
            disabledTooltipMessage={<FormattedMessage {...messages.disabledTooltip} />}
            onClick={onLaunchFromButtonClick}
            testId='rate-browser-launcher'
            theme='tertiary'
          >
            <Text type={TextType.Caption} value={messages.browseRates} />
          </Button>
          {isOpen && (
            <ModalPortal onClose={onClose}>
              <RateViewerModal
                onBrowseRates={onBrowseRates}
                onClose={onClose}
                onConfigureLabel={onConfigureLabel}
                onScaleRead={onScaleMessage}
                rateViewerKey={fulfillmentPlanId}
                shipmentConfiguration={modelFromRateBrowser}
              />
            </ModalPortal>
          )}
        </OpenRateBrowserHotkey>
      </>
    );
  }

  return (
    <>
      <OpenRateBrowserHotkey
        fulfillmentPlanId={fulfillmentPlanId}
        isOnOrderDetails={isOnOrderDetails}
        onLaunchRateBrowserFromOrderDetails={onLaunch}
      >
        <Tooltip position='top'>
          <Button
            className={classnames(style['rate-browser-button'], className)}
            disabled={isExternalFulfillment}
            disabledTooltipMessage={<FormattedMessage {...messages.disabledTooltip} />}
            onClick={onLaunchFromButtonClick}
            testId='rate-browser-launcher'
            theme='unstyled'
          >
            <FontAwesomeIcon className={classnames(style.icon, iconClassName)} icon={faCalculator} />
          </Button>
          <TooltipContent>
            <FormattedMessage {...messages.browseRates} />
          </TooltipContent>
        </Tooltip>
        {isOpen && (
          <ModalPortal onClose={onClose}>
            <RateViewerModal
              isMultiPackageActive={packages.length > 1}
              onBrowseRates={onBrowseRates}
              onClose={onClose}
              onConfigureLabel={onConfigureLabel}
              onScaleRead={onScaleMessage}
              rateViewerKey={fulfillmentPlanId}
              shipmentConfiguration={modelFromRateBrowser}
            />
          </ModalPortal>
        )}
      </OpenRateBrowserHotkey>
    </>
  );
};

RateBrowserButton.propTypes = {
  className: PropTypes.string,
  forceTextButton: PropTypes.bool,
  iconClassName: PropTypes.string,
  isOnOrderDetails: PropTypes.bool,
};
